import api from '@/lib/axios';
import type { PaginatedResult } from '@/components/data-table';

// ───── Types ────────────────────────────────────────────────────────────────
export interface AcademicYear {
  id: string;
  schoolId: string;
  name: string;
  startDate: string;
  endDate: string;
  isCurrent: boolean;
  isLocked: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface ClassEntity {
  id: string;
  schoolId: string;
  academicYearId: string;
  courseId?: string | null;
  /** Resolved course name (college mode) — null when unassigned/school mode. */
  courseName?: string | null;
  name: string;
  orderIndex: number;
  createdAt: string;
  updatedAt: string;
  sections?: Section[];
}

/**
 * Display label for a class. In college mode classes reuse names like
 * "Semester 1" across courses, so prefix the course to disambiguate
 * (e.g. "B.Sc CS · Semester 1"). Falls back to the bare name.
 */
export function classLabel(cls: {
  name: string;
  courseName?: string | null;
}): string {
  return cls.courseName ? `${cls.courseName} · ${cls.name}` : cls.name;
}

export type CourseLevel =
  | 'ug'
  | 'pg'
  | 'diploma'
  | 'phd'
  | 'certificate'
  | 'other';

export type TermSystem = 'annual' | 'semester' | 'trimester';

export interface Course {
  id: string;
  schoolId: string;
  academicYearId: string;
  level: CourseLevel;
  name: string;
  code: string | null;
  termSystem: TermSystem;
  durationYears: number;
  orderIndex: number;
  classCount?: number;
  classesGenerated?: number;
  createdAt: string;
  updatedAt: string;
}

export interface Section {
  id: string;
  schoolId: string;
  classId: string;
  name: string;
  capacity: number;
  classTeacherId: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface Subject {
  id: string;
  schoolId: string;
  classId: string;
  name: string;
  code: string;
  isOptional: boolean;
  maxMarks: number;
  passMarks: number;
  createdAt: string;
  updatedAt: string;
}

/** The student's linked guardian (master.guardian_directory), read-only here — see AdmitGuardianPayload for creating/editing one via admission. */
export interface StudentGuardian {
  id: string;
  firstName: string | null;
  lastName: string | null;
  email: string | null;
  mobile: string | null;
  gender: string | null;
  username: string | null;
  image: string | null;
}

/** `StudentsService.create/update/findOne()`'s real shape — the migrated `students`/`users`/`student_enrollments` tables. See docs/DEVIATIONS.md. */
export interface ExtraFieldValue {
  fieldId: string;
  name: string;
  type: string;
  value: string | string[] | null;
}

export interface Student {
  id: string;
  extraFieldValues: ExtraFieldValue[];
  admissionNo: string;
  admissionDate: string;
  rollNumber: number | null;
  chessNumber: number | null;
  currentAddress: string | null;
  permanentAddress: string | null;
  userId: string | null;
  name: string | null;
  firstName: string | null;
  username: string | null;
  mobile: string | null;
  dob: string | null;
  gender: 'male' | 'female' | 'other' | null;
  image: string | null;
  enrollmentId: string | null;
  sectionId: string | null;
  sectionName: string | null;
  classId: string | null;
  className: string | null;
  academicYearId: string | null;
  /** Only present on `get()` (findOne). */
  guardian?: StudentGuardian | null;
  /** Only present on `create()` — surface these to the admitting staff member once, they aren't retrievable again. */
  credentials?: {
    studentUsername: string;
    studentPassword: string;
    studentPasswordFallbackApplied: boolean;
    guardianCredentials: { username: string; password: string; passwordFallback: boolean } | null;
  };
}

export interface AdmitGuardianPayload {
  firstName: string;
  lastName?: string;
  /** Dedup key when supplied — an existing Guardian with this email is reused (and refreshed), not duplicated. Optional: some intake flows never collect one. */
  email?: string;
  mobile: string;
  gender: 'male' | 'female';
  username?: string;
  image?: string;
}

export interface CreateStudentPayload {
  firstName: string;
  lastName?: string;
  dob: string;
  gender: 'male' | 'female' | 'other';
  mobile?: string;
  image?: string;
  currentAddress?: string;
  permanentAddress?: string;
  sectionId: string;
  academicYearId: string;
  admissionDate: string;
  /** Auto-suggested (and validated for uniqueness) when omitted. */
  admissionNo?: string;
  /** Auto-numbered (max existing + 1 within the section) when omitted. */
  rollNumber?: number;
  /** Defaults to active when omitted. */
  status?: 'active' | 'inactive';
  extraFields?: { fieldId: string; value: string | string[] }[];
  guardian: AdmitGuardianPayload;
}

export interface UpdateStudentPayload {
  firstName?: string;
  lastName?: string;
  dob?: string;
  gender?: 'male' | 'female' | 'other';
  mobile?: string;
  image?: string;
  currentAddress?: string;
  permanentAddress?: string;
  rollNumber?: number;
  /** Supply together with academicYearId to move the student — creates a new enrollment, retires the old one. */
  sectionId?: string;
  academicYearId?: string;
  extraFields?: { fieldId: string; value: string | string[] }[];
}

export interface User {
  id: string;
  name: string;
  email: string;
  role: 'owner' | 'admin' | 'manager' | 'teacher' | 'staff' | 'cashier';
  roleKey?: string | null;
  isActive: boolean;
  avatarUrl: string | null;
  gender?: string | null;
  dob?: string | null;
}

export interface Staff {
  id: string;
  schoolId: string;
  userId: string;
  employeeId: string;
  designation: string;
  department: string | null;
  qualification: string | null;
  joiningDate: string;
  salary: number;
  bankAccount: string | null;
  bankIfsc: string | null;
  pan: string | null;
  aadhar: string | null;
  address: string | null;
  mobileCountryCode: string | null;
  mobile: string | null;
  whatsappCountryCode: string | null;
  whatsapp: string | null;
  photoUrl: string | null;
  status: 'active' | 'on_leave' | 'resigned' | 'terminated';
  createdAt: string;
  updatedAt: string;
  user?: User | null;
}

export interface SchoolStats {
  teachers: { total: number };
  students: { total: number };
  guardians: { total: number };
  classes: { total: number };
  sections: { total: number };
  subjects: { total: number };
  academicYears: { total: number; current: { id: string; name: string } | null };
}

function unwrap<T>(r: { data: { data?: T } | T }): T {
  const body: any = r.data;
  return (body?.data ?? body) as T;
}

// ───── Pagination & Export helpers ──────────────────────────────────────────
export interface Paginated<T> {
  items: T[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export type ExportFormat = 'xlsx' | 'pdf' | 'csv';

export interface ListParams {
  page?: number;
  limit?: number;
  search?: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  [key: string]: unknown;
}

/** Fetch a file (with auth headers) and trigger a browser download. */
export async function downloadExport(
  path: string,
  baseName: string,
  format: ExportFormat,
  params: Record<string, unknown> = {},
): Promise<void> {
  const res = await api.get(path, {
    params: { ...params, format },
    responseType: 'blob',
  });
  const blob = new Blob([res.data]);
  const href = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = href;
  a.download = `${baseName}.${format}`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(href);
}

// ───── Settings: academic terminology ───────────────────────────────────────
export interface Terminology {
  level: string;
  levelPlural: string;
  group: string;
  groupPlural: string;
  institutionType: 'school' | 'college';
}

export const DEFAULT_TERMINOLOGY: Terminology = {
  level: 'Class',
  levelPlural: 'Classes',
  group: 'Section',
  groupPlural: 'Sections',
  institutionType: 'school',
};

export type RoleAccessMap = Record<string, string[]>;
export interface MenuAccess {
  roleAccess: RoleAccessMap;
}

// ───── RBAC: roles & permissions ────────────────────────────────────────────
export type PermAction = 'list' | 'create' | 'delete';
export interface PermModule {
  key: string;
  label: string;
  group: string;
  actions: PermAction[];
}
export interface MePerms {
  role: string;
  isSystem: boolean;
  isAdmin: boolean;
  permissions: string[];
}
export interface RoleView {
  id: string | null;
  key: string;
  name: string;
  description: string | null;
  isSystem: boolean;
  permissions: string[];
  userCount: number;
}
export interface PermissionCatalog {
  modules: PermModule[];
  permissions: string[];
}

// ───── Billing ──────────────────────────────────────────────────────────────
export interface BillingPlanRef {
  id: string;
  name: string;
  slug: string;
  priceMonthly: number;
  priceYearly: number;
  isCustom: boolean;
  isFeatured?: boolean;
  features?: string[];
  maxStudents?: number;
  maxStaff?: number;
}
export interface BillingInvoice {
  invoiceNumber: string;
  amount: number;
  currency: string;
  status: string;
  paidAt: string | null;
  createdAt: string;
}
export interface BillingInfo {
  status: string;
  isTrial: boolean;
  trialStartsAt: string | null;
  trialEndsAt: string | null;
  trialDaysLeft: number | null;
  subscriptionEndsAt: string | null;
  plan: BillingPlanRef | null;
  subscription: {
    status: string;
    billingCycle: 'monthly' | 'yearly';
    amount: number;
    currency: string;
    currentPeriodEnd: string | null;
    cancelAtPeriodEnd: boolean;
    paymentGateway: string | null;
  } | null;
  invoices: BillingInvoice[];
  availablePlans: BillingPlanRef[];
  gatewayConfigured: boolean;
  razorpayKeyId: string;
}
export interface CheckoutOrder {
  orderId: string;
  amount: number;
  currency: string;
  keyId: string;
  planName: string;
}
export const BillingApi = {
  get: async (): Promise<BillingInfo> => unwrap(await api.get('/school/billing')),
  checkout: async (
    planId: string,
    billingCycle: 'monthly' | 'yearly',
  ): Promise<CheckoutOrder> =>
    unwrap(await api.post('/school/billing/checkout', { planId, billingCycle })),
  verify: async (payload: {
    planId: string;
    billingCycle: 'monthly' | 'yearly';
    razorpayOrderId: string;
    razorpayPaymentId: string;
    razorpaySignature: string;
  }): Promise<BillingInfo> =>
    unwrap(await api.post('/school/billing/verify', payload)),
};

export const RbacApi = {
  me: async (): Promise<MePerms> => unwrap(await api.get('/school/me')),
  roles: async (): Promise<RoleView[]> =>
    unwrap(await api.get('/school/roles')),
  catalog: async (): Promise<PermissionCatalog> =>
    unwrap(await api.get('/school/roles/catalog')),
  createRole: async (data: {
    name: string;
    description?: string;
    permissions: string[];
  }): Promise<RoleView> => unwrap(await api.post('/school/roles', data)),
  updateRole: async (
    id: string,
    data: { name?: string; description?: string; permissions?: string[] },
  ): Promise<RoleView> => unwrap(await api.patch(`/school/roles/${id}`, data)),
  deleteRole: async (id: string) =>
    unwrap(await api.delete(`/school/roles/${id}`)),
};

export const NOTIFICATION_CATEGORIES = [
  'attendance_absent',
  'fee_payment',
  'exam_result',
  'leave_status',
  'tc_issued',
  'assignment_published',
  'announcement',
] as const;
export type NotificationCategory = (typeof NOTIFICATION_CATEGORIES)[number];
export type NotificationSettings = Record<NotificationCategory, boolean>;

export const SettingsApi = {
  getTerminology: async (): Promise<Terminology> =>
    unwrap(await api.get('/school/settings/terminology')),
  setTerminology: async (data: Partial<Terminology>): Promise<Terminology> =>
    unwrap(await api.put('/school/settings/terminology', data)),
  getMenuAccess: async (): Promise<MenuAccess> =>
    unwrap(await api.get('/school/settings/menu-access')),
  setMenuAccess: async (roleAccess: RoleAccessMap): Promise<MenuAccess> =>
    unwrap(await api.put('/school/settings/menu-access', { roleAccess })),
  getNotificationSettings: async (): Promise<NotificationSettings> =>
    unwrap(await api.get('/school/settings/notifications')),
  setNotificationSettings: async (data: Partial<NotificationSettings>): Promise<NotificationSettings> =>
    unwrap(await api.put('/school/settings/notifications', data)),
};

// ───── Visitor management ───────────────────────────────────────────────────
export type VisitorGender = 'male' | 'female' | 'other';

export interface VisitorStudentRef {
  id: string;
  admissionNumber: string;
  studentName: string;
}

export interface Visitor {
  id: string;
  schoolId: string;
  studentId: string;
  name: string;
  relation: string | null;
  gender: VisitorGender | null;
  mobile: string;
  email: string | null;
  place: string | null;
  address: string | null;
  idProofType: string | null;
  idProofNumber: string | null;
  photoUrl: string | null;
  notes: string | null;
  isBlacklisted: boolean;
  createdAt: string;
  updatedAt: string;
  student: VisitorStudentRef | null;
}

export interface VisitorLookup {
  id: string;
  name: string;
  mobile: string;
  relation: string | null;
  isBlacklisted: boolean;
  studentId: string;
  student: VisitorStudentRef | null;
}

export type VisitStatus =
  | 'requested'
  | 'approved'
  | 'rejected'
  | 'checked_in'
  | 'checked_out'
  | 'cancelled'
  | 'no_show';
export interface Visit {
  id: string;
  schoolId: string;
  visitorId: string;
  studentId: string;
  meetingWith: string | null;
  purpose: string;
  reason: string | null;
  partySize: number;
  vehicleNumber: string | null;
  scheduledDate: string;
  scheduledTime: string | null;
  status: VisitStatus;
  approvedBy: string | null;
  approvedAt: string | null;
  rejectionReason: string | null;
  passNumber: string | null;
  checkInAt: string | null;
  checkOutAt: string | null;
  durationMinutes: number | null;
  belongings: string | null;
  remarks: string | null;
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
  visitor: {
    id: string;
    name: string;
    mobile: string;
    relation: string | null;
    isBlacklisted: boolean;
  } | null;
  student: VisitorStudentRef | null;
}

export interface VisitSummary {
  currentlyInside: number;
  pendingRequests: number;
  scheduledToday: number;
}

export interface VisitorListParams extends ListParams {
  gender?: VisitorGender;
  blacklisted?: string;
  studentId?: string;
}
export interface VisitListParams extends ListParams {
  status?: VisitStatus;
  visitorId?: string;
  studentId?: string;
  dateFrom?: string;
  dateTo?: string;
}

export const VisitorsApi = {
  list: async (params?: VisitorListParams): Promise<Paginated<Visitor>> =>
    unwrap(await api.get('/school/visitors', { params })),
  lookup: async (params?: {
    search?: string;
    studentId?: string;
  }): Promise<VisitorLookup[]> =>
    unwrap(await api.get('/school/visitors/lookup', { params })),
  create: async (data: Record<string, unknown>): Promise<Visitor> =>
    unwrap(await api.post('/school/visitors', data)),
  update: async (id: string, data: Record<string, unknown>): Promise<Visitor> =>
    unwrap(await api.patch(`/school/visitors/${id}`, data)),
  remove: async (id: string) =>
    unwrap(await api.delete(`/school/visitors/${id}`)),
  export: (format: ExportFormat, params?: VisitorListParams) =>
    downloadExport('/school/visitors/export', 'visitors', format, params),
  importTemplate: () =>
    downloadExport(
      '/school/visitors/import/template',
      'visitor-import-template',
      'xlsx',
    ),
  importPreview: async (file: File): Promise<ImportPreview> => {
    const fd = new FormData();
    fd.append('file', file);
    return unwrap(await api.post('/school/visitors/import/preview', fd));
  },
  importCommit: async (file: File): Promise<ImportCommitResult> => {
    const fd = new FormData();
    fd.append('file', file);
    return unwrap(await api.post('/school/visitors/import/commit', fd));
  },
};

export const VisitsApi = {
  list: async (params?: VisitListParams): Promise<Paginated<Visit>> =>
    unwrap(await api.get('/school/visits', { params })),
  summary: async (): Promise<VisitSummary> =>
    unwrap(await api.get('/school/visits/summary')),
  create: async (data: Record<string, unknown>): Promise<Visit> =>
    unwrap(await api.post('/school/visits', data)),
  approve: async (id: string): Promise<Visit> =>
    unwrap(await api.post(`/school/visits/${id}/approve`)),
  reject: async (id: string, rejectionReason?: string): Promise<Visit> =>
    unwrap(await api.post(`/school/visits/${id}/reject`, { rejectionReason })),
  checkIn: async (
    id: string,
    data: { passNumber?: string; belongings?: string; checkInAt?: string },
  ): Promise<Visit> =>
    unwrap(await api.post(`/school/visits/${id}/check-in`, data)),
  checkOut: async (id: string, data: { remarks?: string }): Promise<Visit> =>
    unwrap(await api.post(`/school/visits/${id}/check-out`, data)),
  cancel: async (id: string): Promise<Visit> =>
    unwrap(await api.post(`/school/visits/${id}/cancel`)),
  noShow: async (id: string): Promise<Visit> =>
    unwrap(await api.post(`/school/visits/${id}/no-show`)),
  export: (format: ExportFormat, params?: VisitListParams) =>
    downloadExport('/school/visits/export', 'visit-history', format, params),
};

// ───── Stats ────────────────────────────────────────────────────────────────
export const SchoolStatsApi = {
  overview: async (): Promise<SchoolStats> =>
    unwrap(await api.get('/school/stats')),
};

// ───── Academic Years ───────────────────────────────────────────────────────
export const AcademicYearsApi = {
  list: async (): Promise<AcademicYear[]> =>
    unwrap<{ items: AcademicYear[] }>(await api.get('/academic-years')).items,
  create: async (data: Partial<AcademicYear>): Promise<AcademicYear> =>
    unwrap(await api.post('/school/academic-years', data)),
  update: async (
    id: string,
    data: Partial<AcademicYear>,
  ): Promise<AcademicYear> =>
    unwrap(await api.patch(`/school/academic-years/${id}`, data)),
  setCurrent: async (id: string): Promise<AcademicYear> =>
    unwrap(await api.post(`/school/academic-years/${id}/set-current`)),
  lock: async (id: string): Promise<AcademicYear> =>
    unwrap(await api.post(`/school/academic-years/${id}/lock`)),
  unlock: async (id: string): Promise<AcademicYear> =>
    unwrap(await api.post(`/school/academic-years/${id}/unlock`)),
  remove: async (id: string) =>
    unwrap(await api.delete(`/school/academic-years/${id}`)),
  copyStructure: async (
    id: string,
    fromYearId: string,
  ): Promise<{
    from: string;
    to: string;
    courses: number;
    classes: number;
    sections: number;
  }> =>
    unwrap(
      await api.post(`/school/academic-years/${id}/copy-structure`, {
        fromYearId,
      }),
    ),
};

// ───── Classes & Sections ───────────────────────────────────────────────────
export const ClassesApi = {
  list: async (academicYearId?: string): Promise<ClassEntity[]> =>
    unwrap(
      await api.get('/school/classes', {
        params: academicYearId ? { academicYearId } : undefined,
      }),
    ),
  listWithSections: async (
    academicYearId?: string,
    courseId?: string,
  ): Promise<ClassEntity[]> =>
    unwrap(
      await api.get('/school/classes', {
        params: {
          withSections: 'true',
          ...(academicYearId ? { academicYearId } : {}),
          ...(courseId ? { courseId } : {}),
        },
      }),
    ),
  create: async (data: Partial<ClassEntity>): Promise<ClassEntity> =>
    unwrap(await api.post('/school/classes', data)),
  update: async (
    id: string,
    data: Partial<ClassEntity>,
  ): Promise<ClassEntity> =>
    unwrap(await api.patch(`/school/classes/${id}`, data)),
  remove: async (id: string) =>
    unwrap(await api.delete(`/school/classes/${id}`)),
  export: (format: ExportFormat, academicYearId?: string) =>
    downloadExport('/school/classes/export', 'classes', format, {
      ...(academicYearId ? { academicYearId } : {}),
    }),
};

export const CoursesApi = {
  list: async (academicYearId?: string): Promise<Course[]> =>
    unwrap(
      await api.get('/school/courses', {
        params: academicYearId ? { academicYearId } : undefined,
      }),
    ),
  create: async (data: {
    academicYearId: string;
    level: CourseLevel;
    name: string;
    code?: string;
    termSystem?: TermSystem;
    durationYears?: number;
    orderIndex?: number;
  }): Promise<Course> => unwrap(await api.post('/school/courses', data)),
  update: async (
    id: string,
    data: Partial<{
      level: CourseLevel;
      name: string;
      code: string;
      termSystem: TermSystem;
      durationYears: number;
      orderIndex: number;
    }>,
  ): Promise<Course> => unwrap(await api.patch(`/school/courses/${id}`, data)),
  remove: async (id: string) =>
    unwrap(await api.delete(`/school/courses/${id}`)),
};

export const SectionsApi = {
  list: async (classId?: string): Promise<Section[]> =>
    unwrap(
      await api.get('/school/sections', {
        params: classId ? { classId } : undefined,
      }),
    ),
  create: async (data: Partial<Section>): Promise<Section> =>
    unwrap(await api.post('/school/sections', data)),
  update: async (id: string, data: Partial<Section>): Promise<Section> =>
    unwrap(await api.patch(`/school/sections/${id}`, data)),
  remove: async (id: string) =>
    unwrap(await api.delete(`/school/sections/${id}`)),
};

// ───── Subjects ─────────────────────────────────────────────────────────────
export const SubjectsApi = {
  list: async (classId?: string): Promise<Subject[]> =>
    unwrap(
      await api.get('/school/subjects', {
        params: classId ? { classId } : undefined,
      }),
    ),
  create: async (data: Partial<Subject>): Promise<Subject> =>
    unwrap(await api.post('/school/subjects', data)),
  update: async (id: string, data: Partial<Subject>): Promise<Subject> =>
    unwrap(await api.patch(`/school/subjects/${id}`, data)),
  remove: async (id: string) =>
    unwrap(await api.delete(`/school/subjects/${id}`)),
  export: (format: ExportFormat, classId?: string) =>
    downloadExport('/school/subjects/export', 'subjects', format, {
      ...(classId ? { classId } : {}),
    }),
  importTemplate: () =>
    downloadExport(
      '/school/subjects/import/template',
      'subject-import-template',
      'xlsx',
    ),
  importPreview: async (
    file: File,
    academicYearId?: string,
  ): Promise<ImportPreview> => {
    const fd = new FormData();
    fd.append('file', file);
    return unwrap(
      await api.post('/school/subjects/import/preview', fd, {
        params: academicYearId ? { academicYearId } : undefined,
      }),
    );
  },
  importCommit: async (
    file: File,
    academicYearId?: string,
  ): Promise<ImportCommitResult> => {
    const fd = new FormData();
    fd.append('file', file);
    return unwrap(
      await api.post('/school/subjects/import/commit', fd, {
        params: academicYearId ? { academicYearId } : undefined,
      }),
    );
  },
};

// ───── Students ─────────────────────────────────────────────────────────────
/** Backed by the still-old-shape `lookup()` endpoint (not repointed by the admission/edit rework) — see docs/DEVIATIONS.md. */
export interface StudentLookup {
  id: string;
  admissionNumber: string;
  studentName: string;
  status: 'active' | 'inactive' | 'transferred' | 'alumni';
}

export interface StudentListParams extends ListParams {
  academicYearId?: string;
  unassigned?: boolean;
  classId?: string;
  sectionId?: string;
  status?: string;
  admissionYear?: number;
  /** Include "left" enrollments (status = 0) — hidden by default, matching the old system. */
  includeLeft?: boolean;
}

/**
 * `StudentsApi.list()`'s real shape, from `StudentsService.migratedList()` —
 * the migrated `students`/`student_enrollments` tables (legacy_id, no
 * school_id/dateOfBirth/gender). Distinct from `Student` above, which is
 * still what `.get()`/`.create()`/`.update()` return (old, currently
 * broken-against-real-data shape) — see docs/DEVIATIONS.md. Only `.list()`
 * is repointed so far.
 */
export interface MigratedStudent {
  id: string;
  legacyId: number;
  admissionNo: string;
  rollNumber: number | null;
  userId: string | null;
  name: string | null;
  /** true only when userId is null — a legacy account-link corruption, not a blank field. */
  nameMissing: boolean;
  gender: string | null;
  dob: string | null;
  username: string | null;
  admissionDate: string | null;
  address: string | null;
  photo: string | null;
  guardian: {
    id: string;
    name: string;
    mobile: string | null;
    username: string | null;
    gender: string | null;
    image: string | null;
  } | null;
  enrolled: boolean;
  /** Decoded from the legacy `promote_students.status` column comment (1=>continue, 0=>leave). null when unassigned. */
  enrollmentStatus: 'continue' | 'left' | null;
  /** Decoded from the legacy `promote_students.result` column comment (1=>Pass, 0=>fail). null when unassigned. */
  enrollmentResult: 'pass' | 'fail' | null;
  classId: string | null;
  className: string | null;
  sectionId: string | null;
  sectionName: string | null;
}

export const StudentsApi = {
  list: async (params?: StudentListParams): Promise<Paginated<MigratedStudent>> =>
    unwrap(await api.get('/school/students', { params })),
  lookup: async (params?: {
    search?: string;
    status?: string;
  }): Promise<StudentLookup[]> =>
    unwrap(await api.get('/school/students/lookup', { params })),
  admissionYears: async (): Promise<number[]> =>
    unwrap(await api.get('/school/students/admission-years')),
  get: async (id: string): Promise<Student> =>
    unwrap(await api.get(`/school/students/${id}`)),
  nextAdmissionNumber: async (academicYearId?: string): Promise<{ admissionNo: string; username: string }> =>
    unwrap(await api.get('/school/students/next-admission-number', { params: { academicYearId } })),
  create: async (data: CreateStudentPayload): Promise<Student> =>
    unwrap(await api.post('/school/students', data)),
  update: async (
    id: string,
    data: UpdateStudentPayload,
  ): Promise<Student> =>
    unwrap(await api.patch(`/school/students/${id}`, data)),
  remove: async (id: string) =>
    unwrap(await api.delete(`/school/students/${id}`)),
  deleteAll: async (): Promise<{ deleted: number; related: number }> =>
    unwrap(await api.delete('/school/students/all')),
  setPin: async (id: string, pin: string) =>
    unwrap(await api.post(`/school/students/${id}/portal-pin`, { pin })),
  removePin: async (id: string) =>
    unwrap(await api.delete(`/school/students/${id}/portal-pin`)),
  export: (format: ExportFormat, params?: StudentListParams) =>
    downloadExport('/school/students/export', 'students', format, params),
  importTemplate: () =>
    downloadExport(
      '/school/students/import/template',
      'student-import-template',
      'xlsx',
    ),
  importInspect: async (file: File): Promise<ImportInspect> => {
    const fd = new FormData();
    fd.append('file', file);
    return unwrap(await api.post('/school/students/import/inspect', fd));
  },
  importPreview: async (
    file: File,
    academicYearId?: string,
    mapping?: ImportMapping,
    duplicates: DuplicateMode = 'skip',
    classId?: string,
    sectionId?: string,
  ): Promise<ImportPreview> => {
    const fd = new FormData();
    fd.append('file', file);
    if (mapping) fd.append('mapping', JSON.stringify(mapping));
    fd.append('duplicates', duplicates);
    if (classId) fd.append('classId', classId);
    if (sectionId) fd.append('sectionId', sectionId);
    return unwrap(
      await api.post('/school/students/import/preview', fd, {
        params: academicYearId ? { academicYearId } : undefined,
      }),
    );
  },
  importCommit: async (
    file: File,
    academicYearId?: string,
    mapping?: ImportMapping,
    duplicates: DuplicateMode = 'skip',
    classId?: string,
    sectionId?: string,
  ): Promise<ImportCommitResult> => {
    const fd = new FormData();
    fd.append('file', file);
    if (mapping) fd.append('mapping', JSON.stringify(mapping));
    fd.append('duplicates', duplicates);
    if (classId) fd.append('classId', classId);
    if (sectionId) fd.append('sectionId', sectionId);
    return unwrap(
      await api.post('/school/students/import/commit', fd, {
        params: academicYearId ? { academicYearId } : undefined,
      }),
    );
  },
  transfer: async (data: TransferStudentsPayload): Promise<TransferStudentsResult> =>
    unwrap(await api.post('/school/students/transfer', data)),
  promote: async (data: PromoteStudentsPayload): Promise<PromoteStudentsResult> =>
    unwrap(await api.post('/school/students/promote', data)),
};

export interface TransferStudentsPayload {
  studentIds: string[];
  fromSectionId: string;
  toSectionId: string;
}

export interface TransferStudentsResult {
  transferred: number;
  students: { studentId: string; admissionNo: string; enrollmentId: string; rollNumber: number }[];
}

export type PromotionResult = 'pass' | 'fail';
export type PromotionStatus = 'continue' | 'left';

export interface PromoteStudentsPayload {
  fromSectionId: string;
  toSectionId: string;
  newAcademicYearId: string;
  students: { studentId: string; result: PromotionResult; status: PromotionStatus }[];
}

export interface PromoteStudentsResult {
  processed: number;
  students: {
    studentId: string;
    admissionNo: string;
    outcome: 'promoted' | 'repeating' | 'left';
    enrollmentId: string | null;
    rollNumber: number | null;
  }[];
}

// ───── Uploads (images / PDFs) ───────────────────────────────────────────────
export interface UploadResult {
  url: string;
  name: string;
  size: number;
  mime: string;
}
export const UploadApi = {
  upload: async (file: File): Promise<UploadResult> => {
    const fd = new FormData();
    fd.append('file', file);
    return unwrap(await api.post('/school/uploads', fd));
  },
};

// ───── Student qualifications (prior education) ──────────────────────────────
export interface StudentQualification {
  id: string;
  schoolId: string;
  studentId: string;
  examName: string;
  board: string | null;
  institution: string | null;
  yearOfPassing: number | null;
  percentage: string | null;
  grade: string | null;
  registerNumber: string | null;
  fileUrl: string | null;
  orderIndex: number;
  createdAt: string;
  updatedAt: string;
}
export const QualificationsApi = {
  list: async (studentId: string): Promise<StudentQualification[]> =>
    unwrap(
      await api.get('/school/student-qualifications', {
        params: { studentId },
      }),
    ),
  create: async (data: Record<string, unknown>): Promise<StudentQualification> =>
    unwrap(await api.post('/school/student-qualifications', data)),
  update: async (
    id: string,
    data: Record<string, unknown>,
  ): Promise<StudentQualification> =>
    unwrap(await api.patch(`/school/student-qualifications/${id}`, data)),
  remove: async (id: string) =>
    unwrap(await api.delete(`/school/student-qualifications/${id}`)),
};

// ───── Student documents (proofs / certificates) ────────────────────────────
export type StudentDocumentType =
  | 'aadhaar'
  | 'birth_certificate'
  | 'transfer_certificate'
  | 'marksheet'
  | 'id_proof'
  | 'address_proof'
  | 'caste_certificate'
  | 'income_certificate'
  | 'photo'
  | 'other';
export interface StudentDocument {
  id: string;
  schoolId: string;
  studentId: string;
  type: StudentDocumentType;
  title: string;
  fileUrl: string;
  fileName: string | null;
  note: string | null;
  createdAt: string;
  updatedAt: string;
}
export const DocumentsApi = {
  list: async (studentId: string): Promise<StudentDocument[]> =>
    unwrap(
      await api.get('/school/student-documents', { params: { studentId } }),
    ),
  create: async (data: Record<string, unknown>): Promise<StudentDocument> =>
    unwrap(await api.post('/school/student-documents', data)),
  update: async (
    id: string,
    data: Record<string, unknown>,
  ): Promise<StudentDocument> =>
    unwrap(await api.patch(`/school/student-documents/${id}`, data)),
  remove: async (id: string) =>
    unwrap(await api.delete(`/school/student-documents/${id}`)),
};

// ───── Academics: bulk-enroll & promotion ───────────────────────────────────
export type DuplicateMode = 'skip' | 'import';
export interface ImportRowResult {
  rowNumber: number;
  data: Record<string, string>;
  errors: string[];
  warnings: string[];
  willEnroll: boolean;
  willImport: boolean;
  duplicate: boolean;
  autoAdmissionNumber: boolean;
}
export interface ImportPreview {
  rows: ImportRowResult[];
  summary: {
    total: number;
    valid: number;
    invalid: number;
    duplicates: number;
  };
}
export interface ImportCommitResult {
  created: number;
  skipped: number;
  errors: { rowNumber: number; error: string }[];
}
export interface ImportField {
  key: string;
  label: string;
  required: boolean;
}
export interface ImportInspect {
  headers: string[];
  fields: ImportField[];
  suggested: Record<string, string | null>;
}
/** field key → exact Excel header text that supplies it. */
export type ImportMapping = Record<string, string>;

export interface PromotionSourceClass {
  id: string;
  name: string;
  courseName?: string | null;
  orderIndex: number;
  activeStudents: number;
}
export interface PromotionClassStudent {
  id: string;
  admissionNumber: string;
  studentName: string;
  rollNumber: string | null;
}
export type PromotionAction = 'promote' | 'detain' | 'transfer';
export interface PromotionDecision {
  studentId: string;
  action: PromotionAction;
  toClassId?: string;
  toSectionId?: string;
  rollNumber?: string;
}

export const AcademicsApi = {
  bulkEnroll: async (data: {
    academicYearId: string;
    classId: string;
    sectionId: string;
    studentIds: string[];
    startRoll?: number;
  }): Promise<{ assigned: number; errors: unknown[] }> =>
    unwrap(await api.post('/school/academics/bulk-enroll', data)),
  promotionSource: async (
    academicYearId: string,
  ): Promise<PromotionSourceClass[]> =>
    unwrap(
      await api.get('/school/academics/promotion/source', {
        params: { academicYearId },
      }),
    ),
  classStudents: async (
    academicYearId: string,
    classId: string,
  ): Promise<PromotionClassStudent[]> =>
    unwrap(
      await api.get('/school/academics/promotion/class-students', {
        params: { academicYearId, classId },
      }),
    ),
  promote: async (data: {
    fromAcademicYearId: string;
    toAcademicYearId: string;
    decisions: PromotionDecision[];
  }): Promise<{ promoted: number; detained: number; transferred: number }> =>
    unwrap(await api.post('/school/academics/promote', data)),
};

// ───── Parents / Guardians ──────────────────────────────────────────────────
// Backed by master.guardian_directory + guardian_children (NOT a per-school
// `parents` table — that table was never provisioned for any real school;
// see ParentsService's doc comment). `id` here is the guardian_children
// LINK id (one row per student↔guardian relationship), not the shared
// guardian record. `relation`/whatsapp/occupation/annualIncome/aadharNumber/
// isPrimary/portal-PIN/import don't exist in this model (real legacy never
// had them either, except occupation — see backend doc comment) and were
// deliberately dropped rather than faked.
// `id` is the guardian's own id (guardian_directory.id) — one row per
// DISTINCT guardian, matching real legacy's own guardian-centric list
// (confirmed via GuardianController::show()). A guardian with multiple
// children at this school appears once, with all of them in `students`.
export interface Parent {
  id: string;
  schoolId: string;
  name: string;
  gender: 'male' | 'female' | null;
  phone: string | null;
  email: string | null;
  photoUrl: string | null;
  createdAt: string;
  students: {
    id: string;
    admissionNumber: string;
    studentName: string | null;
  }[];
}

export interface ParentListParams extends ListParams {
  studentId?: string;
}

export const ParentsApi = {
  list: async (params?: ParentListParams): Promise<Paginated<Parent>> =>
    unwrap(await api.get('/school/parents', { params })),
  create: async (data: Record<string, unknown>): Promise<Parent> =>
    unwrap(await api.post('/school/parents', data)),
  update: async (id: string, data: Record<string, unknown>): Promise<Parent> =>
    unwrap(await api.patch(`/school/parents/${id}`, data)),
  remove: async (id: string) =>
    unwrap(await api.delete(`/school/parents/${id}`)),
  export: (format: ExportFormat, params?: ParentListParams) =>
    downloadExport('/school/parents/export', 'parents', format, params),
};

// ───── Transfer Certificates ─────────────────────────────────────────────────
export type TcReason =
  | 'transfer'
  | 'completion'
  | 'expulsion'
  | 'withdrawal'
  | 'other';
export type TcConduct = 'excellent' | 'good' | 'satisfactory' | 'poor';

export interface TransferCertificate {
  id: string;
  schoolId: string;
  studentId: string;
  tcNumber: string;
  issueDate: string;
  reason: TcReason;
  lastClass: string;
  conduct: TcConduct;
  feesCleared: boolean;
  pdfUrl: string | null;
  issuedBy: string;
  createdAt: string;
  student: {
    id: string;
    admissionNumber: string;
    studentName: string;
    status: 'active' | 'inactive' | 'transferred' | 'alumni';
  } | null;
}

export interface IssueTcPayload {
  studentId: string;
  reason: TcReason;
  conduct?: TcConduct;
  feesCleared?: boolean;
  issueDate?: string;
  lastClass?: string;
}

export interface TcListParams extends ListParams {
  reason?: TcReason;
}

export const TransferCertificatesApi = {
  list: async (
    params?: TcListParams,
  ): Promise<Paginated<TransferCertificate>> =>
    unwrap(await api.get('/school/transfer-certificates', { params })),
  get: async (id: string): Promise<TransferCertificate> =>
    unwrap(await api.get(`/school/transfer-certificates/${id}`)),
  issue: async (payload: IssueTcPayload): Promise<TransferCertificate> =>
    unwrap(await api.post('/school/transfer-certificates', payload)),
  regeneratePdf: async (id: string) =>
    unwrap(await api.post(`/school/transfer-certificates/${id}/pdf`)),
  revoke: async (id: string) =>
    unwrap(await api.delete(`/school/transfer-certificates/${id}`)),
  export: (format: ExportFormat, params?: TcListParams) =>
    downloadExport(
      '/school/transfer-certificates/export',
      'transfer-certificates',
      format,
      params,
    ),
};

// ───── Attendance ───────────────────────────────────────────────────────────
export type AttendanceStatus =
  | 'present'
  | 'absent'
  | 'late'
  | 'holiday'
  | 'half_day';

export interface SectionAttendanceRow {
  studentId: string;
  admissionNumber: string;
  studentName: string;
  rollNumber: string | null;
  status: AttendanceStatus | null;
  note: string | null;
  attendanceId: string | null;
}

export interface SectionAttendance {
  sectionId: string;
  className: string;
  sectionName: string;
  date: string;
  rows: SectionAttendanceRow[];
  summary: Record<AttendanceStatus, number>;
}

export const AttendanceApi = {
  getSection: async (
    sectionId: string,
    date: string,
  ): Promise<SectionAttendance> =>
    unwrap(
      await api.get(`/school/attendance/section/${sectionId}`, {
        params: { date },
      }),
    ),
  bulkMark: async (data: {
    sectionId: string;
    academicYearId: string;
    date: string;
    entries: {
      studentId: string;
      status: AttendanceStatus;
      note?: string;
    }[];
  }): Promise<{ saved: number; date: string; sectionId: string }> =>
    unwrap(await api.post('/school/attendance/bulk', data)),
  studentSummary: async (
    studentId: string,
    academicYearId?: string,
  ): Promise<Record<string, number>> =>
    unwrap(
      await api.get(`/school/attendance/student/${studentId}/summary`, {
        params: academicYearId ? { academicYearId } : undefined,
      }),
    ),

  // ── Reporting (read-only, over the migrated `attendance`/`staff_attendance`
  // tables) — bare `/attendance/*` routes, distinct backend controllers from
  // the marking endpoints above but grouped here since it's the same
  // frontend-facing "Attendance" API. See docs/DEVIATIONS.md. ──
  defaultYear: async (): Promise<DefaultAcademicYear | null> =>
    unwrap(await api.get('/attendance/default-year')),

  defaultClassId: async (): Promise<string | null> =>
    unwrap(await api.get('/attendance/default-class')),

  studentRoster: async (
    params: StudentRosterParams,
  ): Promise<PaginatedResult<StudentAttendanceRosterRow>> =>
    unwrap(await api.get('/attendance/students', { params })),

  studentsSummary: async (
    params: StudentSummaryParams,
  ): Promise<PaginatedResult<StudentAttendanceSummaryRow>> =>
    unwrap(await api.get('/attendance/students/summary', { params })),

  staffRoster: async (
    params: StaffRosterParams,
  ): Promise<PaginatedResult<StaffRosterRow>> =>
    unwrap(await api.get('/attendance/staff', { params })),

  staffPeople: async (role?: StaffRosterRole): Promise<StaffPerson[]> =>
    unwrap(await api.get('/attendance/staff/people', { params: role ? { role } : undefined })),

  markStaffAttendance: async (
    data: MarkStaffAttendanceData,
  ): Promise<{ id: string; personId: string; date: string; type: 0 | 1 }> =>
    unwrap(await api.post('/attendance/staff/mark', data)),

  staffSummary: async (
    params: StaffSummaryParams,
  ): Promise<PaginatedResult<StaffAttendanceSummaryRow>> =>
    unwrap(await api.get('/attendance/staff/summary', { params })),

  teacherSummary: async (
    params: StaffSummaryParams,
  ): Promise<PaginatedResult<StaffAttendanceSummaryRow>> =>
    unwrap(await api.get('/attendance/teachers/summary', { params })),

  adminList: async (
    params: StaffListParams,
  ): Promise<PaginatedResult<StaffAttendanceRow>> =>
    unwrap(await api.get('/attendance/admins', { params })),

  studentsDaily: async (
    params: StudentDailyParams,
  ): Promise<PaginatedResult<StudentAttendanceDailyRow>> =>
    unwrap(await api.get('/attendance/students/daily', { params })),

  teachersDaily: async (
    params: StaffDailyParams,
  ): Promise<PaginatedResult<StaffAttendanceDailyRow>> =>
    unwrap(await api.get('/attendance/teachers/daily', { params })),

  byClass: async (classId: string, academicYearId?: string): Promise<AttendanceByClass> =>
    unwrap(await api.get('/attendance/by-class', { params: { classId, academicYearId } })),
};

export interface AttendanceByClassSection {
  sectionId: string;
  sectionName: string;
  present: number;
  absent: number;
  percentage: number | null;
}

export interface AttendanceByClass {
  academicYearId: string | null;
  sections: AttendanceByClassSection[];
}

export interface DefaultAcademicYear {
  id: string;
  name: string;
  startDate: string;
  endDate: string;
  /** Most recent date within this year that actually has attendance data — never default a single-date filter to "today". */
  latestDate: string;
}

export interface StudentRosterParams {
  page?: number;
  limit?: number;
  search?: string;
  academicYearId?: string;
  classId?: string;
  sectionId?: string;
  date?: string;
  type?: AttendanceStatus;
}

export interface StudentAttendanceRosterRow {
  studentId: string;
  admissionNo: string;
  rollNumber: number | null;
  studentName: string;
  status: AttendanceStatus | null;
  attendanceId: string | null;
  /** 'left' rows only appear because they have a REAL record on the viewed date (historical) — a left student with no record for this date is excluded entirely, not shown blank. */
  enrollmentStatus: 'continue' | 'left' | null;
}

export interface StudentSummaryParams {
  page?: number;
  limit?: number;
  search?: string;
  academicYearId?: string;
  classId?: string;
  sectionId?: string;
  startDate?: string;
  endDate?: string;
}

export interface StudentAttendanceSummaryRow {
  studentId: string;
  admissionNo: string;
  rollNumber: number | null;
  studentName: string;
  present: number;
  absent: number;
  late: number;
  holiday: number;
  halfDay: number;
  workingDays: number;
}

export interface StaffListParams {
  page?: number;
  limit?: number;
  search?: string;
  date?: string;
}

export interface StaffSummaryParams {
  page?: number;
  limit?: number;
  search?: string;
  startDate?: string;
  endDate?: string;
}

export interface StaffAttendanceRow {
  id: string;
  personId: string;
  personName: string;
  type: number;
  typeLabel: string;
  date: string;
  time: string | null;
  remarks: string | null;
  addedByName: string | null;
}

export type StaffRosterRole = 'teacher' | 'staff' | 'admin';

export interface StaffRosterParams {
  page?: number;
  limit?: number;
  search?: string;
  date?: string;
  role?: StaffRosterRole;
}

/** A roster row always represents a real person — `id`/`type`/`typeLabel` are null when they have no attendance row for the viewed date ("Not Marked"), not omitted. */
export interface StaffRosterRow {
  id: string | null;
  personId: string;
  personName: string;
  role: StaffRosterRole;
  type: number | null;
  typeLabel: string;
  date: string;
  time: string | null;
  remarks: string | null;
  addedByName: string | null;
}

export interface StaffPerson {
  id: string;
  name: string;
  role: StaffRosterRole;
}

export interface MarkStaffAttendanceData {
  personId: string;
  type: 0 | 1;
  date: string;
  remarks?: string;
}

export interface StaffAttendanceSummaryRow {
  personId: string;
  personName: string;
  present: number;
  absent: number;
  other: number;
  workingDays: number;
}

export interface StudentDailyParams {
  page?: number;
  limit?: number;
  search?: string;
  academicYearId?: string;
  classId?: string;
  sectionId?: string;
  studentId?: string;
  startDate?: string;
  endDate?: string;
  type?: AttendanceStatus;
}

/** One row per real attendance event (student, date) — no "not marked" placeholders, unlike StudentAttendanceRosterRow. */
export interface StudentAttendanceDailyRow {
  studentId: string;
  admissionNo: string;
  rollNumber: number | null;
  studentName: string;
  className: string;
  sectionName: string;
  date: string;
  status: AttendanceStatus;
  attendanceId: string;
}

export interface StaffDailyParams {
  page?: number;
  limit?: number;
  search?: string;
  personId?: string;
  startDate?: string;
  endDate?: string;
}

/** One row per real staff_attendance event — no "not marked" placeholders, unlike StaffRosterRow. */
export interface StaffAttendanceDailyRow {
  personId: string;
  personName: string;
  type: number;
  typeLabel: string;
  date: string;
  time: string | null;
  remarks: string | null;
}

// ───── Staff ────────────────────────────────────────────────────────────────
export interface StaffListParams extends ListParams {
  status?: string;
  department?: string;
  /** Filters to the linked user's role (built-in role only — not a custom roleKey). */
  role?: string;
}

export const StaffApi = {
  list: async (params?: StaffListParams): Promise<Paginated<Staff>> =>
    unwrap(await api.get('/school/staff', { params })),
  get: async (id: string): Promise<Staff> =>
    unwrap(await api.get(`/school/staff/${id}`)),
  create: async (data: Record<string, unknown>): Promise<Staff> =>
    unwrap(await api.post('/school/staff', data)),
  update: async (id: string, data: Record<string, unknown>): Promise<Staff> =>
    unwrap(await api.patch(`/school/staff/${id}`, data)),
  remove: async (id: string) => unwrap(await api.delete(`/school/staff/${id}`)),
  export: (format: ExportFormat, params?: StaffListParams) =>
    downloadExport('/school/staff/export', 'staff', format, params),
  importTemplate: () =>
    downloadExport(
      '/school/staff/import/template',
      'staff-import-template',
      'xlsx',
    ),
  importPreview: async (file: File): Promise<ImportPreview> => {
    const fd = new FormData();
    fd.append('file', file);
    return unwrap(await api.post('/school/staff/import/preview', fd));
  },
  importCommit: async (file: File): Promise<ImportCommitResult> => {
    const fd = new FormData();
    fd.append('file', file);
    return unwrap(await api.post('/school/staff/import/commit', fd));
  },
};

// ───── Staff documents ──────────────────────────────────────────────────────
export type StaffDocumentType =
  | 'aadhaar'
  | 'pan'
  | 'id_proof'
  | 'address_proof'
  | 'resume'
  | 'certificate'
  | 'qualification'
  | 'experience'
  | 'contract'
  | 'photo'
  | 'other';
export interface StaffDocument {
  id: string;
  schoolId: string;
  staffId: string;
  type: StaffDocumentType;
  title: string;
  fileUrl: string;
  fileName: string | null;
  note: string | null;
  createdAt: string;
  updatedAt: string;
}
export const StaffDocumentsApi = {
  list: async (staffId: string): Promise<StaffDocument[]> =>
    unwrap(await api.get('/school/staff-documents', { params: { staffId } })),
  create: async (data: Record<string, unknown>): Promise<StaffDocument> =>
    unwrap(await api.post('/school/staff-documents', data)),
  update: async (
    id: string,
    data: Record<string, unknown>,
  ): Promise<StaffDocument> =>
    unwrap(await api.patch(`/school/staff-documents/${id}`, data)),
  remove: async (id: string) =>
    unwrap(await api.delete(`/school/staff-documents/${id}`)),
};

// ───── Exams & Marks ────────────────────────────────────────────────────────
// Rewritten against the real exams/exam_timetables/exam_marks/exam_results
// schema — the old ExamType/classId/single-subject-grid shape queried
// tables that never existed in any real school (see backend
// exams.service.ts header comment).
//
// `status` (draft/published) and `scheduleStatus` (upcoming/ongoing/
// completed) are two DISTINCT concepts, never conflated: `status` is a
// stored publish flag; `scheduleStatus` is computed live from this exam's
// own exam_timetables dates (legacy Exam.php:89-135), never stored, no
// manual override — matching legacy exactly.
export type ExamStatus = 'draft' | 'published';
export type ExamScheduleStatus = 'upcoming' | 'ongoing' | 'completed';

export interface Exam {
  id: string;
  academicYearId: string;
  name: string;
  startDate: string;
  endDate: string;
  status: ExamStatus;
  scheduleStatus: ExamScheduleStatus;
  createdAt: string;
  updatedAt: string;
}

export interface ExamTimetableEntry {
  id: string;
  examId: string;
  classSubjectId: string;
  classId: string;
  className: string;
  subjectId: string;
  subjectName: string;
  date: string;
  startTime: string;
  endTime: string;
  totalMarks: number;
  passingMarks: number;
}

export interface MarksGridSubject {
  classSubjectId: string;
  subjectName: string;
  totalMarks: number;
  passingMarks: number;
}

export interface MarksGridStudent {
  id: string;
  admissionNumber: string;
  studentName: string;
  rollNumber: number | null;
}

export interface MarksGrid {
  exam: { id: string; name: string; status: ExamStatus; scheduleStatus: ExamScheduleStatus; academicYearId: string };
  classId: string;
  subjects: MarksGridSubject[];
  students: MarksGridStudent[];
  /** Keyed by `${studentId}:${classSubjectId}` */
  marks: Record<string, { obtainedMarks: number; passingStatus: boolean; grade: string }>;
}

export interface ExamResultRow {
  studentId: string;
  studentName: string;
  totalObtained: number;
  totalMax: number;
  overallPercent: number;
  cumulativeGrade: string;
  finalPassStatus: boolean;
}

export interface MissingMarksEntry {
  examTimetableId: string;
  subjectName: string;
  className: string;
  studentId: string;
  studentName: string;
}

export interface ExamResultsByClassRow {
  classId: string;
  className: string;
  total: number;
  passed: number;
  failed: number;
  avgPercent: number | null;
}

export const ExamsApi = {
  list: async (params?: { academicYearId?: string; status?: ExamStatus }): Promise<Exam[]> =>
    unwrap(await api.get('/school/exams', { params })),
  get: async (id: string): Promise<Exam> =>
    unwrap(await api.get(`/school/exams/${id}`)),
  create: async (data: {
    name: string;
    academicYearId: string;
    startDate: string;
    endDate: string;
  }): Promise<Exam> => unwrap(await api.post('/school/exams', data)),
  update: async (
    id: string,
    data: Partial<{ name: string; startDate: string; endDate: string }>,
  ): Promise<Exam> => unwrap(await api.patch(`/school/exams/${id}`, data)),
  remove: async (id: string) => unwrap(await api.delete(`/school/exams/${id}`)),

  listTimetable: async (id: string): Promise<ExamTimetableEntry[]> =>
    unwrap(await api.get(`/school/exams/${id}/timetable`)),
  createTimetableEntry: async (
    id: string,
    data: {
      classSubjectId: string;
      date: string;
      startTime: string;
      endTime: string;
      totalMarks: number;
      passingMarks: number;
    },
  ): Promise<ExamTimetableEntry> =>
    unwrap(await api.post(`/school/exams/${id}/timetable`, data)),
  removeTimetableEntry: async (id: string, ttId: string) =>
    unwrap(await api.delete(`/school/exams/${id}/timetable/${ttId}`)),

  marksGrid: async (id: string, classId: string): Promise<MarksGrid> =>
    unwrap(await api.get(`/school/exams/${id}/marks-grid`, { params: { classId } })),
  saveMarksBySubject: async (
    id: string,
    classSubjectId: string,
    marksData: { studentId: string; obtainedMarks: number }[],
  ): Promise<{ saved: number }> =>
    unwrap(await api.post(`/school/exams/${id}/marks/by-subject`, { classSubjectId, marksData })),
  saveMarksByStudent: async (
    id: string,
    studentId: string,
    marksData: { classSubjectId: string; obtainedMarks: number }[],
  ): Promise<{ saved: number }> =>
    unwrap(await api.post(`/school/exams/${id}/marks/by-student`, { studentId, marksData })),

  publish: async (
    id: string,
    override?: boolean,
  ): Promise<{ published: boolean; students: number; missingOverridden: number }> =>
    unwrap(await api.post(`/school/exams/${id}/publish`, { override })),
  unpublish: async (id: string) => unwrap(await api.post(`/school/exams/${id}/unpublish`)),
  results: async (id: string): Promise<ExamResultRow[]> =>
    unwrap(await api.get(`/school/exams/${id}/results`)),
  resultsByClass: async (id: string): Promise<ExamResultsByClassRow[]> =>
    unwrap(await api.get(`/school/exams/${id}/results-by-class`)),
};

// ───── Online Exams ─────────────────────────────────────────────────────────
// Fresh, from-scratch feature — fully separate from Exams/Report Cards
// above (own tables, own routes, no merged aggregation). CORRECTED MODEL:
// a question is a REUSABLE BANK entry (OnlineExamQuestionsApi below),
// scoped to class section + class subject, independent of any one exam —
// linking a bank question into an exam (with that exam's own marks
// weighting) is a separate step (OnlineExamsApi.linkQuestions). Admin/
// teacher authoring lives at /school/online-exams + /school/online-exam-
// questions (staff JWT, these APIs); the timed student-attempt runtime
// lives at /portal/online-exams (PIN JWT, unchanged contract) — see
// online-exams-portal.api.ts.
export type OnlineExamStatus = 'draft' | 'published';

export interface OnlineExam {
  id: string;
  name: string;
  classSectionId: string;
  classSubjectId: string;
  className: string | null;
  sectionName: string | null;
  subjectName: string | null;
  durationMinutes: number;
  startDatetime: string;
  endDatetime: string;
  status: OnlineExamStatus;
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface BankOption {
  id: string;
  optionText: string;
  isAnswer: boolean;
  orderIndex: number;
}

export interface BankQuestion {
  id: string;
  classSectionId: string;
  classSubjectId: string;
  className: string | null;
  sectionName: string | null;
  subjectName: string | null;
  questionText: string;
  imageUrl: string | null;
  note: string | null;
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
  options: BankOption[];
}

/** A bank question as linked into one exam — carries THIS exam's own marks weighting, not the bank question's (which has none). */
export interface LinkedQuestion {
  linkId: string;
  questionId: string;
  marks: number;
  orderIndex: number;
  questionText: string;
  imageUrl: string | null;
  note: string | null;
  options: BankOption[];
}

/** A bank question available to link into an exam — no marks yet (that's assigned at link time), no linkId (not linked yet). */
export interface AvailableQuestion {
  id: string;
  questionText: string;
  imageUrl: string | null;
  note: string | null;
  options: BankOption[];
}

export interface OnlineExamResultRow {
  studentId: string;
  studentName: string;
  rollNumber: number | null;
  status: 'not_started' | 'in_progress' | 'submitted';
  startedAt: string | null;
  submittedAt: string | null;
  score: number | null;
  totalMarks: number;
}

export interface OnlineExamResults {
  exam: { id: string; name: string; status: OnlineExamStatus; totalMarks: number };
  students: OnlineExamResultRow[];
}

export interface InvalidQuestionsError {
  error: 'INVALID_QUESTIONS';
  message: string;
  details: { questionId: string; questionText: string; reason: string }[];
}

export const OnlineExamsApi = {
  list: async (params?: {
    classSectionId?: string;
    classSubjectId?: string;
    academicYearId?: string;
    status?: OnlineExamStatus;
  }): Promise<OnlineExam[]> => unwrap(await api.get('/school/online-exams', { params })),
  get: async (id: string): Promise<OnlineExam> =>
    unwrap(await api.get(`/school/online-exams/${id}`)),
  create: async (data: {
    name: string;
    classSectionId: string;
    classSubjectId: string;
    durationMinutes: number;
    startDatetime: string;
    endDatetime: string;
  }): Promise<OnlineExam> => unwrap(await api.post('/school/online-exams', data)),
  update: async (id: string, data: Partial<{ name: string; durationMinutes: number; startDatetime: string; endDatetime: string }>): Promise<OnlineExam> =>
    unwrap(await api.patch(`/school/online-exams/${id}`, data)),

  listLinkedQuestions: async (id: string): Promise<LinkedQuestion[]> =>
    unwrap(await api.get(`/school/online-exams/${id}/questions`)),
  availableQuestions: async (id: string): Promise<AvailableQuestion[]> =>
    unwrap(await api.get(`/school/online-exams/${id}/available-questions`)),
  linkQuestions: async (id: string, items: { questionId: string; marks: number }[]): Promise<{ linked: number }> =>
    unwrap(await api.post(`/school/online-exams/${id}/link-questions`, { items })),
  updateLinkMarks: async (id: string, questionId: string, marks: number) =>
    unwrap(await api.patch(`/school/online-exams/${id}/questions/${questionId}`, { marks })),
  unlinkQuestion: async (id: string, questionId: string) =>
    unwrap(await api.delete(`/school/online-exams/${id}/questions/${questionId}`)),

  publish: async (id: string): Promise<{ published: boolean; questions: number }> =>
    unwrap(await api.patch(`/school/online-exams/${id}/publish`)),
  results: async (id: string): Promise<OnlineExamResults> =>
    unwrap(await api.get(`/school/online-exams/${id}/results`)),
};

/** THE QUESTION BANK — author/browse/filter independently of any one exam. */
export const OnlineExamQuestionsApi = {
  list: async (params?: { classSectionId?: string; classSubjectId?: string }): Promise<BankQuestion[]> =>
    unwrap(await api.get('/school/online-exam-questions', { params })),
  get: async (id: string): Promise<BankQuestion> =>
    unwrap(await api.get(`/school/online-exam-questions/${id}`)),
  create: async (data: {
    classSectionId: string;
    classSubjectId: string;
    questionText: string;
    imageUrl?: string;
    note?: string;
    options: { optionText: string; isAnswer: boolean }[];
  }): Promise<BankQuestion> => unwrap(await api.post('/school/online-exam-questions', data)),
  update: async (id: string, data: Partial<{ questionText: string; imageUrl: string; note: string }>): Promise<BankQuestion> =>
    unwrap(await api.patch(`/school/online-exam-questions/${id}`, data)),
  remove: async (id: string) => unwrap(await api.delete(`/school/online-exam-questions/${id}`)),

  addOption: async (id: string, data: { optionText: string; isAnswer: boolean }): Promise<BankOption> =>
    unwrap(await api.post(`/school/online-exam-questions/${id}/options`, data)),
  updateOption: async (id: string, optionId: string, data: Partial<{ optionText: string; isAnswer: boolean }>) =>
    unwrap(await api.patch(`/school/online-exam-questions/${id}/options/${optionId}`, data)),
  removeOption: async (id: string, optionId: string) =>
    unwrap(await api.delete(`/school/online-exam-questions/${id}/options/${optionId}`)),
};

// ───── Timetable ────────────────────────────────────────────────────────────
// Reshaped from an earlier period-number/academic-year design (never
// actually provisioned in any real school) to match the real migrated
// legacy data — day/date-keyed, subject_teacher-based. See docs/DEVIATIONS.md.
export type TimetableDay =
  | 'Monday'
  | 'Tuesday'
  | 'Wednesday'
  | 'Thursday'
  | 'Friday'
  | 'Saturday'
  | 'Sunday';

export interface TimetableSlot {
  id: string;
  day: TimetableDay;
  date: string | null;
  startTime: string; // HH:MM
  endTime: string;
  type: 'Lecture' | 'Break';
  subjectId: string | null;
  subject: string | null;
  teacherId: string | null;
  teacher: string;
}

export interface TimetableSectionGrid {
  section: { id: string; name: string; classId: string; className: string };
  days: TimetableDay[];
  slots: Record<string, TimetableSlot[]>;
  hasData: boolean;
}

export interface TimetableTeacherSlot extends TimetableSlot {
  section: string;
}

export interface TimetableTeacherGrid {
  teacher: { id: string; name: string };
  days: TimetableDay[];
  slots: Record<string, TimetableTeacherSlot[]>;
  hasData: boolean;
}

export interface TimetablePaletteTeacher {
  subjectTeacherId: string;
  teacherId: string;
  teacherName: string | null;
}

export interface TimetablePaletteSubject {
  subjectId: string;
  subjectName: string;
  /** Empty when nobody teaches this subject in this section yet. */
  teachers: TimetablePaletteTeacher[];
}

export interface TimetablePalette {
  subjects: TimetablePaletteSubject[];
}

export interface CreateTimetableSlotPayload {
  sectionId: string;
  day: TimetableDay;
  startTime: string;
  endTime: string;
  subjectTeacherId?: string;
  subjectId?: string;
  date?: string;
  /** Acknowledge a prior OVERLAP_WARNING and save anyway. */
  force?: boolean;
}

export interface UpdateTimetableSlotPayload {
  day?: TimetableDay;
  startTime?: string;
  endTime?: string;
  date?: string;
  /** Acknowledge a prior OVERLAP_WARNING and save anyway. */
  force?: boolean;
}

export interface TimetableOverlapConflict {
  id: string;
  startTime: string;
  endTime: string;
  subject: string | null;
  teacher: string | null;
}

/** Shape of `response.data.error` on a 409 when a create/update overlaps an existing slot (warn-but-allow — see timetable.service.ts's assertNoOverlap()). `details` carries the conflicting slots. */
export interface TimetableOverlapError {
  code: 'OVERLAP_WARNING';
  message: string;
  details: TimetableOverlapConflict[];
}

export const TimetableApi = {
  sectionGrid: async (sectionId: string): Promise<TimetableSectionGrid> =>
    unwrap(await api.get('/timetable', { params: { sectionId } })),
  teacherGrid: async (teacherId: string): Promise<TimetableTeacherGrid> =>
    unwrap(await api.get(`/timetable/teacher/${teacherId}`)),
  palette: async (sectionId: string): Promise<TimetablePalette> =>
    unwrap(await api.get('/timetable/palette', { params: { sectionId } })),
  createSlot: async (payload: CreateTimetableSlotPayload): Promise<TimetableSlot> =>
    unwrap(await api.post('/timetable', payload)),
  updateSlot: async (id: string, payload: UpdateTimetableSlotPayload): Promise<TimetableSlot> =>
    unwrap(await api.patch(`/timetable/${id}`, payload)),
  removeSlot: async (id: string): Promise<{ id: string }> =>
    unwrap(await api.delete(`/timetable/${id}`)),
  removeAllForSection: async (sectionId: string): Promise<{ deleted: number }> =>
    unwrap(await api.delete(`/timetable/section/${sectionId}`)),
};

// ───── Biometric devices (school side) ──────────────────────────────────────
export interface BiometricDevice {
  id: string;
  sn: string;
  alias: string | null;
  terminalName: string | null;
  deviceType: string;
  state: string | null;
  ipAddress: string | null;
  userCount: number | null;
  fpCount: number | null;
  faceCount: number | null;
  transactionCount: number | null;
  isApproved: boolean;
  deactivatedAt: string | null;
  lastSyncAt: string | null;
  lastActivity: string | null;
  createdAt: string;
}

export interface BiometricTransaction {
  id: string;
  deviceSn: string;
  userCode: string;
  studentId: string | null;
  staffId: string | null;
  actualPunchTime: string;
  punchTime: string;
  punchState: number;
  punchStateDisplay: string;
  source: string;
  createdAt: string;
}

export interface BiometricEnrollment {
  id: string;
  userCode: string;
  studentId: string | null;
  staffId: string | null;
  deviceSn: string | null;
  type: string;
  index: string;
  valid: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface BiometricStats {
  total_devices: number;
  online_devices: number;
  total_transactions_today: number;
  enrolled_users: number;
}

export interface BiometricCommand {
  id: string;
  sn: string;
  command: string;
  status: number;
  deviceReturnCode: number | null;
  createdAt: string;
}

export interface BiometricTxParams {
  page?: number;
  limit?: number;
  from?: string;
  to?: string;
  studentId?: string;
  staffId?: string;
  punchState?: number;
}

export const BiometricApi = {
  listDevices: async (): Promise<BiometricDevice[]> =>
    unwrap(await api.get('/school/biometric-devices')),
  getDevice: async (id: string): Promise<BiometricDevice> =>
    unwrap(await api.get(`/school/biometric-devices/${id}`)),
  stats: async (): Promise<BiometricStats> =>
    unwrap(await api.get('/school/biometric-devices/stats')),
  transactions: async (
    params?: BiometricTxParams,
  ): Promise<Paginated<BiometricTransaction>> =>
    unwrap(await api.get('/school/biometric-devices/transactions', { params })),
  enrollments: async (params?: {
    page?: number;
    limit?: number;
    type?: string;
    userCode?: string;
  }): Promise<Paginated<BiometricEnrollment>> =>
    unwrap(await api.get('/school/biometric-devices/enrollments', { params })),
  commands: async (id: string): Promise<BiometricCommand[]> =>
    unwrap(await api.get(`/school/biometric-devices/${id}/commands`)),
  rename: async (id: string, alias: string): Promise<BiometricDevice> =>
    unwrap(await api.patch(`/school/biometric-devices/${id}/alias`, { alias })),
  restart: async (id: string) =>
    unwrap(await api.post(`/school/biometric-devices/${id}/restart`, {})),
  syncUsers: async (id: string) =>
    unwrap(await api.post(`/school/biometric-devices/${id}/sync-users`, {})),
  clearData: async (id: string) =>
    unwrap(await api.post(`/school/biometric-devices/${id}/clear-data`, {})),
  deleteTransaction: async (id: string) =>
    unwrap(await api.delete(`/school/biometric-devices/transactions/${id}`)),
};

// ───── Notifications ────────────────────────────────────────────────────────
export type NotificationType = 'all' | 'specific' | 'roles' | 'overdue_fees' | 'specific_class';

export interface NotificationSectionOption {
  id: string;
  name: string;
}

export interface NotificationRow {
  id: string;
  title: string;
  message: string;
  type: NotificationType;
  targetMeta: Record<string, unknown>;
  sentBy: string;
  sentByName: string | null;
  sentAt: string;
  recipientCount: number;
  createdAt: string;
}

export interface NotificationSendResult {
  // null only when `category` was passed and that category is disabled in
  // settings — never happens from the admin compose UI, which never sets it.
  notification: NotificationRow | null;
  recipientCount: number;
  reachableCount: number;
  send: { attempted: boolean; successCount?: number; failureCount?: number; reason?: string };
}

export interface NotificationUserOption {
  id: string;
  name: string;
  role: string;
}

export interface InboxNotification {
  id: string;
  type: string;
  title: string;
  message: string;
  actionUrl: string | null;
  isRead: boolean;
  createdAt: string;
}

export const NotificationsApi = {
  list: async (params?: { page?: number; limit?: number }): Promise<Paginated<NotificationRow>> =>
    unwrap(await api.get('/school/notifications', { params })),
  create: async (data: {
    title: string;
    message: string;
    type: NotificationType;
    userIds?: string[];
    roles?: string[];
    sectionIds?: string[];
  }): Promise<NotificationSendResult> => unwrap(await api.post('/school/notifications', data)),
  remove: async (id: string) => unwrap(await api.delete(`/school/notifications/${id}`)),
  lookupUsers: async (): Promise<NotificationUserOption[]> =>
    unwrap(await api.get('/school/notifications/lookup/users')),
  lookupSections: async (): Promise<NotificationSectionOption[]> =>
    unwrap(await api.get('/school/notifications/lookup/sections')),

  // ── Per-user inbox (bell dropdown) ──────────────────────────────────────
  listInbox: async (params?: { page?: number; limit?: number; isRead?: boolean }): Promise<Paginated<InboxNotification>> =>
    unwrap(await api.get('/school/notifications/inbox', { params })),
  unreadCount: async (): Promise<{ count: number }> =>
    unwrap(await api.get('/school/notifications/inbox/unread-count')),
  markRead: async (id: string): Promise<InboxNotification> =>
    unwrap(await api.patch(`/school/notifications/inbox/${id}/read`)),
  markAllRead: async (): Promise<{ updated: true }> =>
    unwrap(await api.patch('/school/notifications/inbox/read-all')),
  registerPushToken: async (token: string) =>
    unwrap(await api.post('/school/notifications/push-token', { token })),
};

// ───── Announcements ────────────────────────────────────────────────────────
export type AnnouncementType = 'class' | 'subject' | 'noticeboard';

export interface AnnouncementTarget {
  sectionId: string;
  classSubjectId?: string;
}

export interface AnnouncementFileInput {
  fileUrl: string;
  fileName: string;
}

export interface AnnouncementRow {
  id: string;
  title: string;
  description: string;
  academicYearId: string;
  type: AnnouncementType;
  createdBy: string;
  createdByName: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface RecentAnnouncement {
  id: string;
  title: string;
  description: string;
  createdAt: string;
}

export interface AnnouncementSendResult {
  announcement: AnnouncementRow;
  recipientCount: number;
  reachableCount: number;
  send: { attempted: boolean; successCount?: number; failureCount?: number; reason?: string };
}

export const AnnouncementsApi = {
  list: async (params?: {
    type?: AnnouncementType;
    classId?: string;
    subjectId?: string;
    page?: number;
    limit?: number;
  }): Promise<Paginated<AnnouncementRow>> => unwrap(await api.get('/school/announcements', { params })),
  recent: async (limit = 5): Promise<RecentAnnouncement[]> =>
    unwrap(await api.get('/school/announcements/recent', { params: { limit } })),
  create: async (data: {
    title: string;
    description: string;
    academicYearId: string;
    type: AnnouncementType;
    targets?: AnnouncementTarget[];
    files?: AnnouncementFileInput[];
  }): Promise<AnnouncementSendResult> => unwrap(await api.post('/school/announcements', data)),
  update: async (id: string, data: Partial<{ title: string; description: string }>): Promise<AnnouncementRow> =>
    unwrap(await api.patch(`/school/announcements/${id}`, data)),
  remove: async (id: string) => unwrap(await api.delete(`/school/announcements/${id}`)),
};

export const SLIDER_AUDIENCES = ['student', 'teacher', 'all'] as const;
export type SliderAudience = (typeof SLIDER_AUDIENCES)[number];

export interface SliderRow {
  id: string;
  imageUrl: string;
  link: string | null;
  forSlider: string;
  createdAt: string;
  updatedAt: string;
}

export interface CreateSliderData {
  imageUrl: string;
  link?: string;
  forSlider: SliderAudience;
}

export interface UpdateSliderData {
  imageUrl?: string;
  link?: string;
  forSlider?: SliderAudience;
}

export const SlidersApi = {
  list: async (): Promise<SliderRow[]> => unwrap(await api.get('/school/sliders')),
  create: async (data: CreateSliderData): Promise<SliderRow> => unwrap(await api.post('/school/sliders', data)),
  update: async (id: string, data: UpdateSliderData): Promise<SliderRow> => unwrap(await api.patch(`/school/sliders/${id}`, data)),
  remove: async (id: string): Promise<void> => unwrap(await api.delete(`/school/sliders/${id}`)),
};

export interface AffidavitStudentOption {
  id: string;
  name: string;
}

export interface AffidavitRow {
  id: string;
  studentId: string;
  studentName: string;
  className: string;
  sectionName: string;
  guardianId: string | null;
  guardianName: string | null;
  studentSignatureUrl: string | null;
  guardianSignatureUrl: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CreateAffidavitData {
  studentId: string;
  studentSignatureUrl: string;
  guardianSignatureUrl: string;
}

export interface UpdateAffidavitData {
  studentSignatureUrl?: string;
  guardianSignatureUrl?: string;
}

export const AffidavitsApi = {
  list: async (): Promise<AffidavitRow[]> => unwrap(await api.get('/school/affidavits')),
  studentsBySection: async (sectionId: string): Promise<AffidavitStudentOption[]> =>
    unwrap(await api.get('/school/affidavits/students-by-section', { params: { sectionId } })),
  create: async (data: CreateAffidavitData): Promise<AffidavitRow> => unwrap(await api.post('/school/affidavits', data)),
  update: async (id: string, data: UpdateAffidavitData): Promise<AffidavitRow> => unwrap(await api.patch(`/school/affidavits/${id}`, data)),
  remove: async (id: string): Promise<void> => unwrap(await api.delete(`/school/affidavits/${id}`)),
};
